import { IMetricDashboard, ITag } from "../interfaces";
import {
  DEFAULT_CALLBACK_NOTIFICATIONS,
  DEFAULT_COMPOSITION_FILE_NAME,
  DEFAULT_SMS_MESSAGE,
  DEFAULT_AI_VISION_PROMPT,
  ScheduleTypeEnum,
  ScreenShareSurfaceEnum,
} from "../../core-ui.enums";
import {
  AppointmentRoutingTypeEnum,
  IAppointmentNotification,
} from "../Appointment";
import { ChatModeEnum } from "@auvious/integrations";
import { TicketCharacterModeEnum } from "../ITicket";

/**
 * What the agent's single "check media authenticity" action captures. Image
 * and audio are on Reality Defender's free tier; video needs a plan that
 * covers it and is already RD's combined video+audio scan, hence no
 * "video+audio". `image+audio` is two scans per check.
 */
export type DeepfakeCheckMode = "image" | "audio" | "image+audio" | "video";

export const DEEPFAKE_CHECK_MODES: readonly DeepfakeCheckMode[] = [
  "image",
  "audio",
  "image+audio",
  "video"
];

export const DEFAULT_DEEPFAKE_CHECK_MODE: DeepfakeCheckMode = "image";

/**
 * Automatic check: seconds after a customer's stream appears before the
 * check runs on it. Long enough to skip a participant who leaves at once
 * or is still fixing their camera, short enough to matter.
 */
export const DEFAULT_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS = 10;
export const MIN_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS = 1;
export const MAX_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS = 300;

/**
 * Clip checks (audio, image+audio, video): seconds the tile is recorded for.
 * Reality Defender strips silence and then needs at least 1.5 s of speech, so
 * a quiet participant needs a longer window, not a louder one. The floor is
 * where a clip can still pass at all; the ceiling is for the video clip (the
 * only heavy one): the ai's 48 MB body limit allows ~9 Mbps over 30 s, which
 * a webcam clip should stay under — not measured against a real camera.
 */
export const DEFAULT_DEEPFAKE_CLIP_SECONDS = 6;
export const MIN_DEEPFAKE_CLIP_SECONDS = 2;
export const MAX_DEEPFAKE_CLIP_SECONDS = 30;

/**
 * Clip checks can hold the recording until the participant is heard speaking
 * (the tile's voice detection), so the window opens on a voice instead of on
 * a quiet moment. Bounded: after this many seconds without a voice the clip
 * records regardless, and Reality Defender reports it as too short.
 */
export const DEFAULT_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS = 30;
export const MIN_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS = 1;
export const MAX_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS = 300;

/**
 * How long a check waits for Reality Defender's verdict before it is reported
 * as timed out. Mirrors the ai service, which is what enforces the range (and
 * falls back to its own 150 s default on a missing or invalid value); the UI
 * only validates the form. Long enough for a slow video scan (several minutes
 * when RD is busy), and capped because every waiting check holds a server slot.
 */
export const DEFAULT_DEEPFAKE_RESULT_TIMEOUT_SECONDS = 150;
export const MIN_DEEPFAKE_RESULT_TIMEOUT_SECONDS = 30;
export const MAX_DEEPFAKE_RESULT_TIMEOUT_SECONDS = 600;

/**
 * A seconds setting read out of the untyped config map: clamped to its range,
 * or the default when the value is missing or garbage. `Number(null)` and
 * `Number("")` are 0, which would clamp to the minimum instead of falling back.
 */
export function secondsSetting(
  value: unknown,
  min: number,
  max: number,
  fallback: number
): number {
  const raw =
    value === null || value === undefined || value === "" ? NaN : Number(value);

  return Number.isFinite(raw) ? Math.min(max, Math.max(min, raw)) : fallback;
}

// eslint-disable-next-line no-shadow
export enum AgentParam {
  RECORDER_CONFIGURED = "recorderConfigured",
  RECORDER_AUTO_START = "recorderAutoStart",
  RECORDER_AUDIO_ONLY = "recorderAudioOnly",
  RECORDER_SCREEN_ENABLED = "recorderScreenEnabled",
  RECORDER_LOCK = "recorderLock",
  RATING_REQUIRED = "ratingRequired",
  SCHEDULE_ENABLED = "callbackEnabled",
  CALLBACK_INTERACTION_PREFIX = "callbackInteractionPrefix",
  SCREEN_SHARE_ENABLED = "screenShareEnabled",
  SCREEN_SHARE_WIDGET_ENABLED = "screenShareWidgetEnabled",
  SCREEN_SHARE_SURFACE = "screenShareSurface",
  AUTO_JOIN_CALL = "autoJoinCall",
  CUSTOMER_LANGUAGE = "customerLanguage",
  AUTO_HOLD_ON_BLUR = "autoHoldOnBlur",
  COBROWSE_ENABLED = "cobrowseEnabled",
  COBROWSE_RECORD = "cobrowseRecord",
  SNAPSHOT_TYPES = "snapshotTypes",
  SNAPSHOT_ENABLED = "snapshotEnabled",
  SNAPSHOT_SWITCH_CUSTOMER_CAM_ENABLED = "snapshotSwitchCustomerCamEnabled",
  SNAPSHOT_SEGMENT_ENABLED = "snapshotSegmentEnabled",
  AUTO_START_AR = "autoStartAR",
  STREAM_CONTROLS_ENABLED = "streamControlsEnabled",
  FILE_TRANSFER_ENABLED = "fileTransferEnabled",
  DEVICE_SETUP_ENABLED = "deviceSetupEnabled",
  REPORTING_DASHBOARDS = "reportingDashboards",
  REPORTING_CARBON_METRICS_ENABLED = "reportingCarbonMetricsEnabled",
  VIDEO_CALL_ENABLED = "videoCallEnabled",
  AUTO_ANSWER = "autoAnswer",
  POP_OUT_CALL = "popOutCall",
  /** pop out limited to the interaction widget destination */
  POP_OUT_CALL_INTERACTION_WIDGET = "popOutCallInteractionWidget",
  COMPOSITION_DELETE_ENABLED = "compositionDeleteEnabled",
  VIDEO_CALL_REQUIRES_INTERACTION = "videoCallRequiresInteraction",
  OUTBOUND_EMAIL_INTERACTION_ENABLED = "outboundEmailInteractionEnabled",
  MICROPHONE_ENABLED = "microphoneEnabled",
  CAMERA_ENABLED = "cameraEnabled",
  MICROPHONE_DEFAULT_ON = "microphoneDefaultOn",
  CAMERA_DEFAULT_ON = "cameraDefaultOn",
  DEFAULT_BACKGROUND_EFFECTS_ENABLED = "defaultBackgroundEffectsEnabled",
  SINGLE_USE_TICKET_ENABLED = "singleUseTicketEnabled",
  SINGLE_USE_SCHEDULE_TICKET_ENABLED = "singleUseScheduleTicketEnabled",
  TICKET_LENGTH = "ticketLength",
  TICKET_CHARACTER_MODE = "ticketCharacterMode",
  TICKET_TTL = "ticketTTL",
  TICKET_CALLBACK_TTL = "ticketCallbackTTL",
  COMPOSITION_EXPORT_ENABLED = "compositionExportEnabled",
  COMPOSITION_DOWNLOAD_ENABLED = "compositionDownloadEnabled",
  // wait for X seconds for customer to join the call, if not, it is probably a ghost call
  DISCONNECTED_CUSTOMER_TIMEOUT = "disconnectedCustomerTimeout",
  SCHEDULE_CHANNEL = "scheduleChannel",
  /**
   * @deprecated moved to service params, no need to send them in a new appointment
   */
  APPOINTMENT_NOTIFICATIONS = "appointmentNotifications",
  //----
  APPOINTMENT_NOTIFICATIONS_ENABLED = "appointmentNotificationsEnabled",
  APPOINTMENT_NOTIFICATION_HEADERS_ENABLED = "appointmentNotificationHeadersEnabled",
  APPOINTMENT_METADATA_ENABLED = "appointmentMetadataEnabled",
  APPOINTMENT_REASSIGNMENT_ENABLED = "appointmentReassignmentEnabled",
  // assign a new appointment to another agent. Requires APPOINTMENT_REASSIGNMENT_ENABLED
  APPOINTMENT_ASSIGNMENT_ENABLED = "appointmentAssignmentEnabled",
  APPOINTMENT_CREATION_ENABLED = "appointmentCreationEnabled",
  DEFAULT_BACKGROUND_EFFECT_ID = "defaultBackroundEffectId",
  COBROWSE_SCREEN_SHARE_ENABLED = "cobrowseScreenShareEnabled",
  COBROWSE_AGENT_SCREEN_SHARE_ENABLED = "cobrowseAgentScreenShareEnabled",
  COBROWSE_INTERACTION_AWARE = "cobrowseInteractionAware",
  SNAPSHOT_DEFAULT_TYPES_APPENDED = "snashotDefaultTypesAppended",
  COBROWSE_CONTROL_PERMISSION_ENABLED = "cobrowseControlPermissionEnabled",
  APPOINTMENT_NOTIFICATION_CHANNEL_SMS_ENABLED = "appointmentNotificationChannelSMSEnabled",
  APPOINTMENT_NOTIFICATION_CHANNEL_EMAIL_ENABLED = "appointmentNotificationChannelEmailEnabled",
  CALLBACK_DEFAULT_QUEUE = "callbackDefaultQueue",
  TRANSCRIPT_PROVIDER_CONFIGURED = "asrOfflineProviderConfigured",
  TRANSCRIPT_PROVIDER = "transcriptProvider",
  SMS_WORKFLOW_ENABLED = "smsWorkflowEnabled",
  SMS_WORKFLOW_ID = "smsWorkflowId",
  EMAIL_WORKFLOW_ENABLED = "emailWorkflowEnabled",
  EMAIL_WORKFLOW_ID = "emailWorkflowId",
  DIGITAL_CONNECT_ENABLED = "digitalConnectEnabled",
  LEAVE_CONTROL_ENABLED = "leaveControlEnabled",
  APPOINTMENT_CHAT_MODE = "appointmentChatMode",
  BACKGROUND_EFFECT_PER_QUEUE_ENABLED = "backgroundEffectPerQueueEnabled",
  BACKGROUND_EFFECT_PER_QUEUE = "backgroundEffectPerQueue",
  AUTO_INVITE_CUSTOMER = "autoInviteCustomer",
  QR_INVITATION_ENABLED = "qrInvitationEnabled",
  CUSTOMER_INVITATION_ENABLED = "customerInvitationEnabled",
  CUSTOMER_INVITATION_TICKET_VISIBLE = "customerInvitationTicketVisible",
  AGENT_INVITATION_ENABLED = "agentInvitationEnabled",
  CALLBACK_NOTIFICATIONS = "callbackNotifications",
  APPOINTMENT_ROUTING_TYPE = "appointmentRoutingType",
  COMPOSITION_DEFAULT_FILE_NAME = "compositionDefaultFileName",
  CUSTOMER_SCREEN_SHARE_REQUEST_ENABLED = "customerScreenShareRequestEnabled",
  // agent can ask the customer to set the agent in full screen. cannot auto-do it, not allowed by browser, must be a user-initiated action
  CUSTOMER_FULLSCREEN_AGENT_REQUEST_ENABLED = "customerFullScreenAgentRequestEnabled",
  // show a button in cobrowse-launcher to auto-generate a string
  // that is used to invite a customer to cobrowse via a chat interaction in auvious widget
  COBROWSE_MANUAL_CHAT_INVITATION_ENABLED = "cobrowseManualChatInvitationEnabled",
  CUSTOMER_CAMERAS_CONTROL_ENABLED = "customerCamerasControlEnabled",
  // agent can turn the customer's microphone noise suppression on/off during a call
  CUSTOMER_NOISE_SUPPRESSION_CONTROL_ENABLED = "customerNoiseSuppressionControlEnabled",
  // agent can turn noise suppression on/off for their own microphone during a call
  AGENT_NOISE_SUPPRESSION_ENABLED = "agentNoiseSuppressionEnabled",
  COBROWSE_CUSTOMER_NAME_VISIBLE = "cobrowseCustomerNameVisible",
  COBROWSE_ALLOW_LOCAL_NETWORK = "cobrowseAllowLocalNetwork",
  COBROWSE_USE_PROXY = "cobrowseUseProxy",
  APPOINTMENT_DURATION_OPTIONS = "appointmentDurationOptions",
  MAIN_MENU_ENABLED = "mainMenuEnabled",
  AI_VISION_ENABLED = "aiVisionEnabled",
  AI_VISION_PROMPT = "aiVisionPrompt",
  // agent can check a participant's camera feed for signs of synthetic media
  DEEPFAKE_DETECTION_ENABLED = "deepfakeDetectionEnabled",
  // what that check captures; see DeepfakeCheckMode
  DEEPFAKE_CHECK = "deepfakeCheck",
  // seconds a clip check records for; see DEFAULT_DEEPFAKE_CLIP_SECONDS
  DEEPFAKE_CLIP_SECONDS = "deepfakeClipSeconds",
  // hold the clip until the participant is heard speaking, and for how long at most
  DEEPFAKE_CLIP_ON_VOICE = "deepfakeClipOnVoice",
  DEEPFAKE_CLIP_VOICE_WAIT_SECONDS = "deepfakeClipVoiceWaitSeconds",
  // seconds the ai waits for the verdict; see DEFAULT_DEEPFAKE_RESULT_TIMEOUT_SECONDS
  DEEPFAKE_RESULT_TIMEOUT_SECONDS = "deepfakeResultTimeoutSeconds",
  // which result details the ai includes in the event and the badge shows
  DEEPFAKE_SHOW_MODEL_AGREEMENT = "deepfakeShowModelAgreement",
  DEEPFAKE_SHOW_ANALYSED_LENGTH = "deepfakeShowAnalysedLength",
  DEEPFAKE_SHOW_CONTEXT_FLAGS = "deepfakeShowContextFlags",
  DEEPFAKE_SHOW_IMAGE_DESCRIPTION = "deepfakeShowImageDescription",
  // run the check on every customer stream by itself, a while after it appears
  DEEPFAKE_AUTO_CHECK_ENABLED = "deepfakeAutoCheckEnabled",
  DEEPFAKE_AUTO_CHECK_DELAY_SECONDS = "deepfakeAutoCheckDelaySeconds",
  // ...including screen shares (image/video only, a screen has no voice)
  DEEPFAKE_AUTO_CHECK_SCREENS = "deepfakeAutoCheckScreens",
  AI_ASSISTANT_ENABLED = "aiAssistantEnabled",
  TRANSCRIPT_CUSTOM_PROMPT = "transcriptCustomPrompt",
  TRANSCRIPT_CUSTOM_PROMPT_ENABLED = "transcriptCustomPromptEnabled",
  // redirect to this url when call ends
  CALL_ENDED_REDIRECT_URL = "callEndedRedirectUrl",
  // end conversation when agent leaves call
  AUTO_END_CONVERSATION_ON_CALL_END = "autoEndConversationOnCallEnd",
  // hold conversation when agent joins call
  AUTO_HOLD_CONVERSATION_ON_CALL_START = "autoHoldConversationOnCallStart",
  // do not end call of conversation ends (after wrap up)
  AUTO_END_CALL_ON_CONVERSATION_END = "autoEndCallOnConversationEnd",
  INTERACTION_TAGGING_ENABLED = "interactionTaggingEnabled",
  INTERACTION_CUSTOM_TAGGING_ENABLED = "interactionCustomTaggingEnabled",
  INTERACTION_TAGS = "interactionTags",
  RING_LIGHT_ENABLED = "ringLightEnabled",
  INTERACTION_MANUAL_SYNC_ENABLED = "interactionManualSyncEnabled",
  SMS_CUSTOM_MESSAGE = "smsCustomMessage",
}

export interface IApplicationAgentParams {
  [AgentParam.RECORDER_CONFIGURED]?: boolean;
  [AgentParam.RECORDER_AUTO_START]?: boolean;
  [AgentParam.RECORDER_AUDIO_ONLY]?: boolean;
  [AgentParam.RECORDER_SCREEN_ENABLED]?: Boolean;
  [AgentParam.RECORDER_LOCK]?: boolean;
  [AgentParam.RATING_REQUIRED]?: boolean;
  [AgentParam.SCHEDULE_ENABLED]?: boolean;
  [AgentParam.CALLBACK_INTERACTION_PREFIX]?: string;
  [AgentParam.SCREEN_SHARE_ENABLED]?: boolean;
  [AgentParam.SCREEN_SHARE_WIDGET_ENABLED]?: boolean;
  [AgentParam.SCREEN_SHARE_SURFACE]?: ScreenShareSurfaceEnum;
  [AgentParam.AUTO_JOIN_CALL]?: boolean;
  [AgentParam.CUSTOMER_LANGUAGE]?: boolean;
  [AgentParam.AUTO_HOLD_ON_BLUR]?: boolean;
  [AgentParam.COBROWSE_ENABLED]?: boolean;
  [AgentParam.COBROWSE_RECORD]?: boolean;
  [AgentParam.SNAPSHOT_TYPES]?: ITag[];
  [AgentParam.SNAPSHOT_ENABLED]?: boolean;
  [AgentParam.SNAPSHOT_SWITCH_CUSTOMER_CAM_ENABLED]?: boolean;
  [AgentParam.SNAPSHOT_SEGMENT_ENABLED]?: boolean;
  [AgentParam.AUTO_START_AR]?: boolean;
  [AgentParam.STREAM_CONTROLS_ENABLED]?: boolean;
  [AgentParam.FILE_TRANSFER_ENABLED]?: boolean;
  [AgentParam.DEVICE_SETUP_ENABLED]?: boolean;
  [AgentParam.REPORTING_DASHBOARDS]?: IMetricDashboard[];
  [AgentParam.REPORTING_CARBON_METRICS_ENABLED]?: boolean;
  [AgentParam.VIDEO_CALL_ENABLED]?: boolean;
  [AgentParam.AUTO_ANSWER]?: boolean;
  [AgentParam.POP_OUT_CALL]?: boolean;
  [AgentParam.POP_OUT_CALL_INTERACTION_WIDGET]?: boolean;
  [AgentParam.COMPOSITION_DELETE_ENABLED]?: boolean;
  [AgentParam.VIDEO_CALL_REQUIRES_INTERACTION]?: boolean;
  [AgentParam.OUTBOUND_EMAIL_INTERACTION_ENABLED]?: boolean;
  [AgentParam.CAMERA_ENABLED]?: boolean;
  [AgentParam.MICROPHONE_ENABLED]?: boolean;
  [AgentParam.CAMERA_DEFAULT_ON]?: boolean;
  [AgentParam.MICROPHONE_DEFAULT_ON]?: boolean;
  [AgentParam.DEFAULT_BACKGROUND_EFFECTS_ENABLED]?: boolean;
  [AgentParam.SINGLE_USE_TICKET_ENABLED]?: boolean;
  [AgentParam.SINGLE_USE_SCHEDULE_TICKET_ENABLED]?: boolean;
  [AgentParam.TICKET_LENGTH]?: number;
  [AgentParam.TICKET_CHARACTER_MODE]?: TicketCharacterModeEnum;
  [AgentParam.TICKET_TTL]?: number;
  [AgentParam.TICKET_CALLBACK_TTL]?: number;
  [AgentParam.COMPOSITION_EXPORT_ENABLED]?: boolean;
  [AgentParam.COMPOSITION_DOWNLOAD_ENABLED]?: boolean;
  [AgentParam.DISCONNECTED_CUSTOMER_TIMEOUT]?: number;
  [AgentParam.SCHEDULE_CHANNEL]?: ScheduleTypeEnum;
  /**
   * @deprecated  moved to service params, no need to send them in a new appointment
   */
  [AgentParam.APPOINTMENT_NOTIFICATIONS]?: IAppointmentNotification[];
  // a simple agent does not have access to serviceParams, we need a copy of that value to agentParams
  [AgentParam.APPOINTMENT_NOTIFICATIONS_ENABLED]?: boolean;
  [AgentParam.APPOINTMENT_NOTIFICATION_HEADERS_ENABLED]?: boolean;
  [AgentParam.APPOINTMENT_METADATA_ENABLED]?: boolean;
  [AgentParam.APPOINTMENT_REASSIGNMENT_ENABLED]?: boolean;
  [AgentParam.APPOINTMENT_ASSIGNMENT_ENABLED]?: boolean;
  [AgentParam.APPOINTMENT_CREATION_ENABLED]?: boolean;
  [AgentParam.DEFAULT_BACKGROUND_EFFECT_ID]?: string;
  [AgentParam.COBROWSE_SCREEN_SHARE_ENABLED]?: boolean;
  [AgentParam.COBROWSE_AGENT_SCREEN_SHARE_ENABLED]?: boolean;
  [AgentParam.COBROWSE_INTERACTION_AWARE]?: boolean;
  [AgentParam.SNAPSHOT_DEFAULT_TYPES_APPENDED]?: boolean;
  [AgentParam.COBROWSE_CONTROL_PERMISSION_ENABLED]?: boolean;
  [AgentParam.APPOINTMENT_NOTIFICATION_CHANNEL_EMAIL_ENABLED]?: boolean;
  [AgentParam.APPOINTMENT_NOTIFICATION_CHANNEL_SMS_ENABLED]?: boolean;
  [AgentParam.CALLBACK_DEFAULT_QUEUE]?: string;
  [AgentParam.TRANSCRIPT_PROVIDER_CONFIGURED]?: boolean;
  [AgentParam.TRANSCRIPT_PROVIDER]?: string;
  [AgentParam.SMS_WORKFLOW_ENABLED]?: boolean;
  [AgentParam.SMS_WORKFLOW_ID]?: string;
  [AgentParam.EMAIL_WORKFLOW_ENABLED]?: boolean;
  [AgentParam.EMAIL_WORKFLOW_ID]?: string;
  [AgentParam.DIGITAL_CONNECT_ENABLED]?: boolean;
  [AgentParam.LEAVE_CONTROL_ENABLED]?: boolean;
  [AgentParam.APPOINTMENT_CHAT_MODE]?: ChatModeEnum;
  [AgentParam.BACKGROUND_EFFECT_PER_QUEUE_ENABLED]?: boolean;
  [AgentParam.BACKGROUND_EFFECT_PER_QUEUE]?: { [assetId: string]: string };
  [AgentParam.AUTO_INVITE_CUSTOMER]?: boolean;
  [AgentParam.QR_INVITATION_ENABLED]?: boolean;
  [AgentParam.CUSTOMER_INVITATION_ENABLED]?: boolean;
  [AgentParam.CUSTOMER_INVITATION_TICKET_VISIBLE]?: boolean;
  [AgentParam.AGENT_INVITATION_ENABLED]?: boolean;
  [AgentParam.CALLBACK_NOTIFICATIONS]?: IAppointmentNotification[];
  [AgentParam.APPOINTMENT_ROUTING_TYPE]?: AppointmentRoutingTypeEnum;
  [AgentParam.COMPOSITION_DEFAULT_FILE_NAME]?: string;
  [AgentParam.CUSTOMER_SCREEN_SHARE_REQUEST_ENABLED]?: boolean;
  [AgentParam.CUSTOMER_FULLSCREEN_AGENT_REQUEST_ENABLED]?: boolean;
  [AgentParam.COBROWSE_MANUAL_CHAT_INVITATION_ENABLED]?: boolean;
  [AgentParam.CUSTOMER_CAMERAS_CONTROL_ENABLED]?: boolean;
  [AgentParam.CUSTOMER_NOISE_SUPPRESSION_CONTROL_ENABLED]?: boolean;
  [AgentParam.AGENT_NOISE_SUPPRESSION_ENABLED]?: boolean;
  [AgentParam.COBROWSE_CUSTOMER_NAME_VISIBLE]?: boolean;
  [AgentParam.COBROWSE_ALLOW_LOCAL_NETWORK]?: boolean;
  [AgentParam.COBROWSE_USE_PROXY]?: boolean;
  [AgentParam.APPOINTMENT_DURATION_OPTIONS]?: number[];
  [AgentParam.MAIN_MENU_ENABLED]?: boolean;
  [AgentParam.AI_VISION_ENABLED]?: boolean;
  [AgentParam.AI_VISION_PROMPT]?: string;
  [AgentParam.DEEPFAKE_DETECTION_ENABLED]?: boolean;
  [AgentParam.DEEPFAKE_CHECK]?: DeepfakeCheckMode;
  [AgentParam.DEEPFAKE_CLIP_SECONDS]?: number;
  [AgentParam.DEEPFAKE_CLIP_ON_VOICE]?: boolean;
  [AgentParam.DEEPFAKE_CLIP_VOICE_WAIT_SECONDS]?: number;
  [AgentParam.DEEPFAKE_RESULT_TIMEOUT_SECONDS]?: number;
  [AgentParam.DEEPFAKE_SHOW_MODEL_AGREEMENT]?: boolean;
  [AgentParam.DEEPFAKE_SHOW_ANALYSED_LENGTH]?: boolean;
  [AgentParam.DEEPFAKE_SHOW_CONTEXT_FLAGS]?: boolean;
  [AgentParam.DEEPFAKE_SHOW_IMAGE_DESCRIPTION]?: boolean;
  [AgentParam.DEEPFAKE_AUTO_CHECK_ENABLED]?: boolean;
  [AgentParam.DEEPFAKE_AUTO_CHECK_DELAY_SECONDS]?: number;
  [AgentParam.DEEPFAKE_AUTO_CHECK_SCREENS]?: boolean;
  [AgentParam.AI_ASSISTANT_ENABLED]?: boolean;
  [AgentParam.TRANSCRIPT_CUSTOM_PROMPT]?: string;
  [AgentParam.TRANSCRIPT_CUSTOM_PROMPT_ENABLED]?: boolean;
  [AgentParam.CALL_ENDED_REDIRECT_URL]?: string;
  [AgentParam.AUTO_END_CONVERSATION_ON_CALL_END]?: boolean;
  [AgentParam.AUTO_HOLD_CONVERSATION_ON_CALL_START]?: boolean;
  [AgentParam.AUTO_END_CALL_ON_CONVERSATION_END]?: boolean;
  [AgentParam.INTERACTION_TAGGING_ENABLED]?: boolean;
  [AgentParam.INTERACTION_CUSTOM_TAGGING_ENABLED]?: boolean;
  [AgentParam.INTERACTION_TAGS]?: string[];
  [AgentParam.RING_LIGHT_ENABLED]?: boolean;
  [AgentParam.INTERACTION_MANUAL_SYNC_ENABLED]?: boolean;
  [AgentParam.SMS_CUSTOM_MESSAGE]?: string;
}

export class AgentOptions implements IApplicationAgentParams {
  public [AgentParam.RECORDER_CONFIGURED] = false;
  public [AgentParam.RECORDER_AUTO_START] = false;
  public [AgentParam.RECORDER_AUDIO_ONLY] = false;
  public [AgentParam.RECORDER_SCREEN_ENABLED] = true;
  public [AgentParam.RECORDER_LOCK] = false;
  public [AgentParam.RATING_REQUIRED] = false;
  public [AgentParam.SCHEDULE_ENABLED] = true;
  public [AgentParam.CALLBACK_INTERACTION_PREFIX] = null;
  public [AgentParam.SCREEN_SHARE_ENABLED] = true;
  public [AgentParam.SCREEN_SHARE_WIDGET_ENABLED] = false;
  public [AgentParam.SCREEN_SHARE_SURFACE] = ScreenShareSurfaceEnum.all;
  public [AgentParam.AUTO_JOIN_CALL] = false;
  public [AgentParam.CUSTOMER_LANGUAGE] = false;
  public [AgentParam.AUTO_HOLD_ON_BLUR] = false;
  public [AgentParam.COBROWSE_ENABLED] = true;
  public [AgentParam.COBROWSE_RECORD] = false;
  public [AgentParam.SNAPSHOT_TYPES] = [];
  public [AgentParam.SNAPSHOT_ENABLED] = false;
  public [AgentParam.SNAPSHOT_SWITCH_CUSTOMER_CAM_ENABLED] = true;
  public [AgentParam.SNAPSHOT_SEGMENT_ENABLED] = true;
  public [AgentParam.AUTO_START_AR] = false;
  public [AgentParam.STREAM_CONTROLS_ENABLED] = true;
  public [AgentParam.FILE_TRANSFER_ENABLED] = false;
  public [AgentParam.DEVICE_SETUP_ENABLED] = true;
  public [AgentParam.REPORTING_DASHBOARDS] = [];
  public [AgentParam.REPORTING_CARBON_METRICS_ENABLED] = true;
  public [AgentParam.VIDEO_CALL_ENABLED] = true;
  public [AgentParam.AUTO_ANSWER] = false;
  public [AgentParam.POP_OUT_CALL] = false;
  public [AgentParam.POP_OUT_CALL_INTERACTION_WIDGET] = false;
  public [AgentParam.COMPOSITION_DELETE_ENABLED] = false;
  public [AgentParam.VIDEO_CALL_REQUIRES_INTERACTION] = true;
  public [AgentParam.OUTBOUND_EMAIL_INTERACTION_ENABLED] = false;
  public [AgentParam.MICROPHONE_ENABLED] = true;
  public [AgentParam.CAMERA_ENABLED] = true;
  public [AgentParam.MICROPHONE_DEFAULT_ON] = true;
  public [AgentParam.CAMERA_DEFAULT_ON] = true;
  public [AgentParam.DEFAULT_BACKGROUND_EFFECTS_ENABLED] = true;
  public [AgentParam.SINGLE_USE_TICKET_ENABLED] = false;
  public [AgentParam.SINGLE_USE_SCHEDULE_TICKET_ENABLED] = false;
  public [AgentParam.TICKET_LENGTH] = 6;
  public [AgentParam.TICKET_CHARACTER_MODE] = TicketCharacterModeEnum.letters;
  public [AgentParam.TICKET_TTL] = 14400; // seconds
  public [AgentParam.TICKET_CALLBACK_TTL] = 28800; // seconds
  public [AgentParam.COMPOSITION_EXPORT_ENABLED] = true;
  public [AgentParam.COMPOSITION_DOWNLOAD_ENABLED] = true;
  public [AgentParam.DISCONNECTED_CUSTOMER_TIMEOUT] = 0;
  public [AgentParam.SCHEDULE_CHANNEL] = ScheduleTypeEnum.genesysCallback;
  // a simple agent does not have access to serviceParams, we need a copy of that value to agentParams
  public [AgentParam.APPOINTMENT_NOTIFICATIONS_ENABLED] = true;
  public [AgentParam.APPOINTMENT_NOTIFICATION_HEADERS_ENABLED] = false;
  public [AgentParam.APPOINTMENT_METADATA_ENABLED] = false;
  public [AgentParam.APPOINTMENT_REASSIGNMENT_ENABLED] = false;
  public [AgentParam.APPOINTMENT_ASSIGNMENT_ENABLED] = false;
  public [AgentParam.APPOINTMENT_CREATION_ENABLED] = true;
  public [AgentParam.DEFAULT_BACKGROUND_EFFECT_ID] = null;
  public [AgentParam.COBROWSE_SCREEN_SHARE_ENABLED] = false;
  public [AgentParam.COBROWSE_AGENT_SCREEN_SHARE_ENABLED] = false;
  public [AgentParam.COBROWSE_INTERACTION_AWARE] = true;
  public [AgentParam.SNAPSHOT_DEFAULT_TYPES_APPENDED] = true;
  public [AgentParam.COBROWSE_CONTROL_PERMISSION_ENABLED] = true;
  public [AgentParam.APPOINTMENT_NOTIFICATION_CHANNEL_EMAIL_ENABLED] = false;
  public [AgentParam.APPOINTMENT_NOTIFICATION_CHANNEL_SMS_ENABLED] = false;
  public [AgentParam.CALLBACK_DEFAULT_QUEUE] = "";
  public [AgentParam.DIGITAL_CONNECT_ENABLED] = false;
  public [AgentParam.APPOINTMENT_NOTIFICATIONS]: IAppointmentNotification[] =
    [];
  public [AgentParam.TRANSCRIPT_PROVIDER_CONFIGURED] = false;
  public [AgentParam.TRANSCRIPT_PROVIDER] = null;
  public [AgentParam.SMS_WORKFLOW_ENABLED] = false;
  public [AgentParam.SMS_WORKFLOW_ID] = null;
  public [AgentParam.EMAIL_WORKFLOW_ENABLED] = false;
  public [AgentParam.EMAIL_WORKFLOW_ID] = null;
  public [AgentParam.LEAVE_CONTROL_ENABLED] = true;
  // depends on the integration, resolved with getDefaultAppointmentChatMode()
  public [AgentParam.APPOINTMENT_CHAT_MODE]: ChatModeEnum = undefined;
  public [AgentParam.BACKGROUND_EFFECT_PER_QUEUE_ENABLED] = false;
  public [AgentParam.BACKGROUND_EFFECT_PER_QUEUE] = {};
  public [AgentParam.AUTO_INVITE_CUSTOMER] = false;
  public [AgentParam.QR_INVITATION_ENABLED] = true;
  public [AgentParam.CUSTOMER_INVITATION_ENABLED] = true;
  public [AgentParam.CUSTOMER_INVITATION_TICKET_VISIBLE] = false;
  public [AgentParam.CUSTOMER_FULLSCREEN_AGENT_REQUEST_ENABLED] = false;
  public [AgentParam.AGENT_INVITATION_ENABLED] = true;
  public [AgentParam.CALLBACK_NOTIFICATIONS] = [
    ...DEFAULT_CALLBACK_NOTIFICATIONS,
  ];
  public [AgentParam.APPOINTMENT_ROUTING_TYPE] =
    AppointmentRoutingTypeEnum.GENESYS_CLOUD_FLOW;
  public [AgentParam.COMPOSITION_DEFAULT_FILE_NAME] =
    DEFAULT_COMPOSITION_FILE_NAME;
  public [AgentParam.CUSTOMER_SCREEN_SHARE_REQUEST_ENABLED] = false;
  public [AgentParam.COBROWSE_MANUAL_CHAT_INVITATION_ENABLED] = false;
  public [AgentParam.CUSTOMER_CAMERAS_CONTROL_ENABLED] = false;
  public [AgentParam.CUSTOMER_NOISE_SUPPRESSION_CONTROL_ENABLED] = false;
  public [AgentParam.AGENT_NOISE_SUPPRESSION_ENABLED] = false;
  public [AgentParam.COBROWSE_CUSTOMER_NAME_VISIBLE] = true;
  public [AgentParam.COBROWSE_ALLOW_LOCAL_NETWORK] = false;
  public [AgentParam.COBROWSE_USE_PROXY] = false;
  public [AgentParam.APPOINTMENT_DURATION_OPTIONS] = [5, 15, 30];
  public [AgentParam.MAIN_MENU_ENABLED] = true;
  public [AgentParam.AI_VISION_ENABLED] = false;
  public [AgentParam.AI_ASSISTANT_ENABLED] = false;
  public [AgentParam.AI_VISION_PROMPT] = DEFAULT_AI_VISION_PROMPT;
  public [AgentParam.DEEPFAKE_DETECTION_ENABLED] = false;
  public [AgentParam.DEEPFAKE_CHECK] = DEFAULT_DEEPFAKE_CHECK_MODE;
  public [AgentParam.DEEPFAKE_CLIP_SECONDS] = DEFAULT_DEEPFAKE_CLIP_SECONDS;
  public [AgentParam.DEEPFAKE_CLIP_ON_VOICE] = false;
  public [AgentParam.DEEPFAKE_CLIP_VOICE_WAIT_SECONDS] =
    DEFAULT_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS;
  public [AgentParam.DEEPFAKE_RESULT_TIMEOUT_SECONDS] =
    DEFAULT_DEEPFAKE_RESULT_TIMEOUT_SECONDS;
  public [AgentParam.DEEPFAKE_SHOW_MODEL_AGREEMENT] = true;
  public [AgentParam.DEEPFAKE_SHOW_ANALYSED_LENGTH] = true;
  public [AgentParam.DEEPFAKE_SHOW_CONTEXT_FLAGS] = true;
  // a caption of the participant's appearance: opt-in
  public [AgentParam.DEEPFAKE_SHOW_IMAGE_DESCRIPTION] = false;
  // every customer stream costs a scan (two under image+audio): opt-in
  public [AgentParam.DEEPFAKE_AUTO_CHECK_ENABLED] = false;
  public [AgentParam.DEEPFAKE_AUTO_CHECK_DELAY_SECONDS] =
    DEFAULT_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS;
  public [AgentParam.DEEPFAKE_AUTO_CHECK_SCREENS] = true;
  public [AgentParam.TRANSCRIPT_CUSTOM_PROMPT] = undefined;
  public [AgentParam.TRANSCRIPT_CUSTOM_PROMPT_ENABLED] = false;
  public [AgentParam.CALL_ENDED_REDIRECT_URL] = null;
  public [AgentParam.AUTO_END_CONVERSATION_ON_CALL_END] = false;
  public [AgentParam.AUTO_HOLD_CONVERSATION_ON_CALL_START] = false;
  public [AgentParam.AUTO_END_CALL_ON_CONVERSATION_END] = true;
  public [AgentParam.INTERACTION_TAGGING_ENABLED] = false;
  public [AgentParam.INTERACTION_CUSTOM_TAGGING_ENABLED] = false;
  public [AgentParam.INTERACTION_TAGS] = [];
  public [AgentParam.RING_LIGHT_ENABLED] = false;
  public [AgentParam.INTERACTION_MANUAL_SYNC_ENABLED]: false;
  public [AgentParam.SMS_CUSTOM_MESSAGE] = DEFAULT_SMS_MESSAGE;

  static create(options: IApplicationAgentParams) {
    const instance = new AgentOptions();
    let notifications: IAppointmentNotification[] =
      instance.appointmentNotifications;

    // we already have existing notifications so try update those
    if (options?.appointmentNotifications?.length > 0) {
      notifications = options.appointmentNotifications.map((n) => {
        // get the new properties and update the existing ones
        let exists: IAppointmentNotification =
          instance.appointmentNotifications.find((o) => o.id === n.id);
        if (exists) {
          // get old properties and also merge old and new properties
          exists = {
            ...exists,
            ...n,
            textBody: n.message && !n.textBody ? n.message : n.textBody,
          };
          return exists.enabled === undefined
            ? {
                ...exists,
                enabled: true,
              }
            : exists;
        }
        return n;
      });

      if (notifications.filter((n) => n.channel === "email").length === 0) {
        notifications = [
          ...notifications,
          ...instance.appointmentNotifications.filter(
            (n) => n.channel === "email",
          ),
        ];
      }
    }
    return {
      ...instance,
      ...options,
      [AgentParam.APPOINTMENT_NOTIFICATIONS]: notifications,
    };
  }
}
